'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { useGetSiteOfCareAttributesQuery } from 'src/api/redux';
import type { SiteOfCareAttribute } from 'src/api/redux/suggestions/suggestionsApi';

interface EntityAttributeComponentProps {
  componentId: string;
  initialEntityAttribute?: Array<SiteOfCareAttribute | string>;
  initialConnector?: 'ANY' | 'ALL';
  onUpdate: (componentId: string, data: any) => void;
  onRemove: (componentId: string) => void;
  fromPlaceholder?: boolean;
}

const EntityAttributeComponent: React.FC<EntityAttributeComponentProps> = ({
  componentId,
  initialEntityAttribute,
  onUpdate,
  onRemove,
  fromPlaceholder = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const [isNewFromPlaceholder, setIsNewFromPlaceholder] = useState(
    fromPlaceholder && (!initialEntityAttribute || initialEntityAttribute.length === 0)
  );

  const { data: attributes, isLoading } = useGetSiteOfCareAttributesQuery();

  const getInitialValue = (): string | null => {
    if (initialEntityAttribute && initialEntityAttribute.length > 0) {
      const item = initialEntityAttribute[0];
      if (typeof item === 'string') {
        return item;
      }
      return item.value;
    }
    return null;
  };

  const [selectedValue, setSelectedValue] = useState<string | null>(
    getInitialValue()
  );

  const updateRedux = useCallback(
    (attributeValue: string) => {
      onUpdate(componentId, {
        type: 'entity_attribute',
        value: attributeValue,
      });
    },
    [componentId, onUpdate]
  );

  useEffect(() => {
    if (!selectedValue) {
      setIsOpen(true);
    } else {
      updateRedux(selectedValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelect = useCallback(
    (value: string) => {
      setSelectedValue(value);
      setIsOpen(false);
      setIsNewFromPlaceholder(false);
      updateRedux(value);
    },
    [updateRedux]
  );

  const handleClose = useCallback(() => {
    setIsOpen(false);
    if (isNewFromPlaceholder && !selectedValue) {
      onRemove(componentId);
    }
  }, [isNewFromPlaceholder, selectedValue, onRemove, componentId]);

  const handleRemove = useCallback(() => {
    onRemove(componentId);
  }, [componentId, onRemove]);

  const selectedOption = selectedValue
    ? attributes?.find((attr) => attr.value === selectedValue)
    : null;

  if (isLoading) {
    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-1 bg-neutral-100 border border-neutral-200 rounded-full text-xs text-neutral-500"
        data-testid="entity-attribute-loading"
      >
        Loading...
      </span>
    );
  }

  return (
    <span
      className="inline-flex items-center gap-1 text-rose-600 text-sm font-semibold group relative"
      data-testid="entity-attribute-component"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="inline-flex items-center gap-1 hover:text-rose-700 cursor-pointer"
        data-testid="entity-attribute-selector"
        aria-label="Select site of care attribute"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span data-testid="entity-attribute-label">
          {selectedOption?.label || selectedValue || 'Select attribute...'}
        </span>
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            data-testid="entity-attribute-overlay"
            onClick={handleClose}
          />
          <div
            className="absolute top-full left-0 mt-1 bg-white border border-neutral-200 rounded-lg shadow-lg z-50 min-w-48 max-h-64 overflow-y-auto"
            role="listbox"
            aria-label="Site of care attribute options"
          >
            {attributes?.map((attr) => (
              <button
                key={attr.value}
                type="button"
                role="option"
                aria-selected={selectedValue === attr.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(attr.value);
                }}
                className={`w-full px-3 py-2 text-left text-sm hover:bg-rose-50 first:rounded-t-lg last:rounded-b-lg ${
                  selectedValue === attr.value
                    ? 'bg-rose-100 text-rose-700 font-medium'
                    : 'text-neutral-700'
                }`}
                data-testid={`entity-attribute-option-${attr.value}`}
              >
                {attr.label}
              </button>
            ))}
          </div>
        </>
      )}

      <button
        className={`text-xs transition-all ${
          isHovered
            ? 'text-red-500 hover:text-red-700 opacity-100'
            : 'text-gray-400 opacity-0 w-0 group-hover:opacity-100 group-hover:w-auto'
        }`}
        onClick={(e) => {
          e.stopPropagation();
          handleRemove();
        }}
        aria-label="Remove entity attribute"
        data-testid="entity-attribute-remove-button"
      >
        x
      </button>
    </span>
  );
};

export default EntityAttributeComponent;
